// Deep42 API client for intelligent project discovery and enrichment

const DEEP42_API_URL = 'https://deep42.cambrian.network/api/v1/deep42/discovery/search-projects';
const DEEP42_API_KEY = import.meta.env.VITE_DEEP42_API_KEY;

export interface Deep42ProjectData {
  projectSymbol: string;
  projectName: string;
  projectId: string;
  isCanonical: boolean;
  description?: string;
  contractAddress?: string;
  chain?: string;
  githubUrl?: string;
  twitterUrl?: string;
  twitterHandle?: string;
  websiteUrl?: string;
  telegramUrl?: string;
  discordUrl?: string;
  category?: string;
  marketCap?: number;
  priceUsd?: number;
  volume24h?: number;
  holderCount?: number;
  githubStars?: number;
  qualityScore?: number;
  confidenceScore?: number;
  legitimacyScore?: number;
  metadataCompleteness?: number;
  isVerified?: boolean;
  dataSource?: string;
}

export interface Deep42EnrichmentResult {
  data: Deep42ProjectData[];
  count: number;
}

export interface EnrichmentQuality {
  metadataCompleteness: number;  // 0-100%
  confidenceScore: number;        // 0-1
  legitimacyScore: number;        // 0-100
  qualityScore: number;           // 0-1
  isHighQuality: boolean;         // >= 80% legitimacy, >= 95% confidence
}

/**
 * Enrich project metadata via Deep42 intelligent discovery API
 *
 * This function calls Deep42's search-projects endpoint with rich metadata.
 * Deep42 will:
 * 1. Validate all provided metadata against multiple sources
 * 2. Perform AI research to fill missing data
 * 3. Verify on-chain data if contracts provided
 * 4. Calculate legitimacy and quality scores
 * 5. STORE high-quality data back to Spanner automatically!
 *
 * @param params - Project metadata to enrich (symbol, contracts, URLs, etc.)
 * @returns Deep42 enriched project data with quality scores
 */
export async function enrichProjectMetadata(params: {
  symbol: string;
  name?: string;
  contracts?: Array<{ address: string; chain: string }>;
  websiteUrl?: string;
  twitterUrl?: string;
  githubUrl?: string;
}): Promise<Deep42EnrichmentResult | null> {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();

    // Primary query
    queryParams.append('query', params.name || params.symbol);

    // Add all contracts (triggers on-chain verification)
    if (params.contracts && params.contracts.length > 0) {
      params.contracts.forEach((contract) => {
        queryParams.append('contract_address', contract.address);
        queryParams.append('chain', contract.chain);
      });
    }

    // Add social/web URLs (triggers AI research)
    if (params.websiteUrl) {
      queryParams.append('website_url', params.websiteUrl);
    }
    if (params.twitterUrl) {
      queryParams.append('twitter_url', params.twitterUrl);
    }

    // Request GitHub discovery
    queryParams.append('has_github', 'true');

    // Limit to 1 result (we're enriching a specific project)
    queryParams.append('limit', '1');

    console.log('[Deep42 Enrichment] Calling search-projects API...', {
      symbol: params.symbol,
      contracts: params.contracts?.length || 0,
      hasWebsite: !!params.websiteUrl,
      hasTwitter: !!params.twitterUrl,
    });

    const response = await fetch(`${DEEP42_API_URL}?${queryParams.toString()}`, {
      headers: {
        'X-API-KEY': DEEP42_API_KEY,
      },
    });

    if (!response.ok) {
      console.warn('[Deep42 Enrichment] API error:', response.status, response.statusText);
      return null;
    }

    const data: Deep42EnrichmentResult = await response.json();

    if (data.count > 0 && data.data[0]) {
      const project = data.data[0];

      console.log('[Deep42 Enrichment] Success!', {
        symbol: project.projectSymbol,
        metadataCompleteness: project.metadataCompleteness,
        confidenceScore: project.confidenceScore,
        legitimacyScore: project.legitimacyScore,
        dataSource: project.dataSource,
      });

      // Data is automatically stored to Spanner by Deep42 if quality is high!
      // No need to handle storage - Deep42 does it intelligently
    }

    return data;
  } catch (error) {
    console.warn('[Deep42 Enrichment] Failed (non-critical):', error);
    return null;
  }
}

/**
 * Extract quality metrics from Deep42 enrichment result
 */
export function getEnrichmentQuality(result: Deep42EnrichmentResult | null): EnrichmentQuality | null {
  if (!result || result.count === 0) {
    return null;
  }

  const project = result.data[0];

  return {
    metadataCompleteness: project.metadataCompleteness || 0,
    confidenceScore: project.confidenceScore || 0,
    legitimacyScore: project.legitimacyScore || 0,
    qualityScore: project.qualityScore || 0,
    isHighQuality: (project.legitimacyScore || 0) >= 80 && (project.confidenceScore || 0) >= 0.95,
  };
}

/**
 * Format quality score for display
 */
export function formatQualityScore(score: number, type: 'percentage' | 'decimal'): string {
  if (type === 'percentage') {
    return `${Math.round(score)}%`;
  }
  return `${Math.round(score * 100)}%`;
}

/**
 * Get quality badge color based on score
 */
export function getQualityBadgeColor(score: number, type: 'legitimacy' | 'confidence' | 'completeness'): string {
  const threshold = type === 'completeness' ? score : type === 'confidence' ? score * 100 : score;

  if (threshold >= 90) return 'bg-green-100 text-green-800';
  if (threshold >= 80) return 'bg-blue-100 text-blue-800';
  if (threshold >= 70) return 'bg-yellow-100 text-yellow-800';
  if (threshold >= 60) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
}
